import {
  Button,
  FormControl,
  FormLabel,
  HStack,
  ModalFooter,
  SimpleGrid,
  Switch,
  useToast,
  VStack,
} from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import useMutacoesPageGroup from '../../../hooks/useMutacoesPageGroup';
import bannersGridTypes, {
  BannersGridType,
} from '../../../utils/bannersGridTypes';
import montarSchemaAgendamento from '../../../utils/schemaAgendamento';
import sanitizeString from '../../../utils/sanitizeString';
import Datepicker from '../../Form/Datepicker';
import Input from '../../Form/Input';
import Select from '../../Form/Select';
import context from '../context';

interface Props {
  onCreated?: (data: any) => void;
  /** Avisa o modal do tipo escolhido, para a aba Banners acompanhar na hora. */
  onTypeChange?: (tipo: BannersGridType) => void;
}

interface FormData {
  name?: string;
  type: { label: string; value: string };
  schedule: boolean;
  schedule_start?: Date;
  schedule_end?: Date;
}

const formData = yup.object().shape({
  name: yup.string().nullable(),
  type: yup.object().shape({
    value: yup.string().required('Tipo é obrigatório'),
    label: yup.string().required('Tipo é obrigatório'),
  }),
  ...montarSchemaAgendamento(),
});

// Espaçamento, cor de fundo e arredondamento são fixos (layout do Figma): não são configuráveis.

const Form: React.FC<Props> = ({ onCreated, onTypeChange }) => {
  const toast = useToast();

  const { pageGroup, newPageGroupDTO, onPageGroupCreated, onClose, display } =
    React.useContext(context);

  const { formState, handleSubmit, register, control, setValue } =
    useForm<FormData>({ resolver: yupResolver(formData) });

  const {
    createPageGroup,
    updatePageGroup,
    removePageGroup,
    salvando,
    excluindo,
    avisarErroInesperado,
  } = useMutacoesPageGroup();

  const [schedule, setSchedule] = useState(!!pageGroup?.schedule_end);

  const { errors } = formState;

  useEffect(() => {
    if (!pageGroup) {
      setValue('type', bannersGridTypes[0]);
      onTypeChange?.(bannersGridTypes[0].value);
      return;
    }
    if (pageGroup?.schedule_end) {
      setValue('schedule', true);
      setValue('schedule_start', new Date(pageGroup.schedule_start));
      setValue('schedule_end', new Date(pageGroup.schedule_end));
    }
    setValue('name', pageGroup?.name);
    setValue(
      'type',
      bannersGridTypes.find(tipo => tipo.value === pageGroup?.type) as any,
    );
  }, [pageGroup, setValue, onTypeChange]);

  const handleForm: SubmitHandler<FormData> = async data => {
    try {
      const sanitizedObj = sanitizeString(data);
      const { schedule: scheduleData, ...rest } = sanitizedObj;

      const dadosDoGrupo = {
        ...rest,
        type: data.type?.value,
        schedule_end: scheduleData ? rest.schedule_end : null,
        schedule_start: scheduleData ? rest.schedule_start : new Date(),
      };

      if (pageGroup) {
        await updatePageGroup({
          variables: {
            updatePageGroupInput: { ...dadosDoGrupo, id: pageGroup?.id },
          },
        });
        toast({
          title: 'Sucesso!',
          description: 'Grupo editado com sucesso!!',
          status: 'success',
          position: 'top-right',
        });
        return;
      }

      const apiData = await createPageGroup({
        variables: {
          createPageGroupInput: {
            ...dadosDoGrupo,
            ...newPageGroupDTO,
            element_display_id: display?.id,
          },
        },
      });
      if (onCreated) onCreated(apiData);
      if (onPageGroupCreated) onPageGroupCreated(apiData);
      toast({
        title: 'Sucesso!',
        description: 'Grupo criado com sucesso!!',
        status: 'success',
        position: 'top-right',
      });
    } catch (erro) {
      avisarErroInesperado(erro);
    }
  };

  const handleDelete = async () => {
    try {
      const confirmed = window.confirm(
        'Tem certeza que deseja excluir o grupo?',
      );
      if (!confirmed) return;

      await removePageGroup({ variables: { id: pageGroup?.id } });
      toast({
        title: 'Sucesso!',
        description: 'Grupo deletado com sucesso',
        status: 'success',
        position: 'top-right',
      });
      if (onClose) onClose();
    } catch (erro) {
      avisarErroInesperado(erro);
    }
  };

  return (
    <>
      <VStack as="form" spacing="8" pb="6" onSubmit={handleSubmit(handleForm)}>
        <SimpleGrid minChildWidth="200px" spacing="8" w="100%">
          <Input
            label="Título (opcional)"
            error={errors?.name}
            {...register('name')}
          />
          <Select
            isRequired
            name="type"
            label="Tipo"
            control={control}
            options={bannersGridTypes}
            error={errors?.type}
            placeholder="Selecione o formato"
            onChange={(opcao: any) => onTypeChange?.(opcao?.value)}
          />
        </SimpleGrid>

        <FormControl display="flex" alignItems="center">
          <FormLabel htmlFor="schedule" mb="0">
            Agendamento
          </FormLabel>
          <Switch
            id="schedule"
            size="lg"
            colorScheme="brand"
            isChecked={schedule}
            {...register('schedule')}
            onChange={evento => {
              setSchedule(evento.target.checked);
              setValue('schedule', evento.target.checked);
            }}
          />
        </FormControl>

        {schedule && (
          <SimpleGrid minChildWidth="200px" spacing="8" w="100%">
            <Datepicker
              isRequired
              control={control}
              {...register('schedule_start')}
              label="Início"
              showTimeSelect
              error={errors?.schedule_start}
            />
            <Datepicker
              isRequired
              control={control}
              {...register('schedule_end')}
              label="Final"
              showTimeSelect
              error={errors?.schedule_end}
            />
          </SimpleGrid>
        )}
      </VStack>

      <ModalFooter>
        <HStack spacing={4}>
          {!!pageGroup && (
            <Button
              onClick={handleDelete}
              colorScheme="red"
              isLoading={excluindo}
            >
              Excluir
            </Button>
          )}
          <Button onClick={onClose}>Fechar</Button>
          <Button
            colorScheme="brand"
            isLoading={salvando}
            onClick={handleSubmit(handleForm)}
          >
            Salvar
          </Button>
        </HStack>
      </ModalFooter>
    </>
  );
};

export default Form;
